import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type ActiveFilterChip = {
  /** Clé technique du critère (`id_account`, `ip`…), sert de `key` React. */
  key: string;
  /** Nom du champ, ex. « Utilisateur ». */
  field: string;
  /** Valeur LISIBLE, ex. « Jean Dupont » — pas l'id envoyé au serveur. */
  value: string;
  onRemove: () => void;
};

/**
 * Filtres actifs, en puces retirables.
 *
 * Indispensable dès lors qu'on filtre en cliquant dans le tableau : sans cette rangée,
 * l'utilisateur voit une liste réduite sans savoir POURQUOI, ni comment revenir en
 * arrière — le filtre est posé ailleurs que là où il se lit.
 *
 * Chaque puce affiche le LIBELLÉ (« Jean Dupont »), jamais l'identifiant envoyé au
 * serveur : `id_account=7` ne veut rien dire pour un lecteur.
 */
export const ActiveFilterChips = ({
  chips,
  onClearAll,
}: {
  chips: ActiveFilterChip[];
  onClearAll: () => void;
}) => {
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-4">
      <span className="text-sm text-grey-readable">Filtres :</span>

      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-2 rounded-full bg-grey-light px-3 py-1 text-sm text-default-text max-w-full"
        >
          <span className="text-grey-readable shrink-0">{chip.field}</span>
          {/* `min-w-0` : sans lui, un enfant flex refuse de se réduire et une valeur
              longue (nom d'entreprise, user-agent) élargit la puce hors de l'écran. */}
          <span className="font-medium truncate min-w-0" title={chip.value}>
            {chip.value}
          </span>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Retirer le filtre ${chip.field} : ${chip.value}`}
            title="Retirer ce filtre"
            className="shrink-0 text-grey-readable hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-tertiary rounded"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </span>
      ))}

      {/* Au-delà d'un filtre, retirer les puces une à une devient fastidieux. */}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-sm text-grey-readable underline hover:text-default-text focus:outline-none focus:ring-2 focus:ring-tertiary rounded"
        >
          Tout effacer
        </button>
      )}
    </div>
  );
};
